import { useEffect, useState } from "react";
import { Star, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export function RatingStars({ value = 0, size = 4, className = "" }) {
  const rounded = Math.round(value);
  const sizeMap = { 3: "h-3 w-3", 4: "h-4 w-4", 5: "h-5 w-5", 6: "h-6 w-6", 7: "h-7 w-7" };
  const cls = sizeMap[size] || "h-4 w-4";
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`} data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${cls} ${
            n <= rounded ? "text-accent fill-accent" : "text-muted-foreground/30"
          }`}
          strokeWidth={2}
        />
      ))}
    </div>
  );
}

export function RatingSummary({ avg = 0, count = 0, size = 4 }) {
  return (
    <div className="inline-flex items-center gap-2">
      <RatingStars value={avg} size={size} />
      <span className="text-sm font-mono-brand font-semibold">
        {count > 0 ? avg.toFixed(1) : "—"}
      </span>
      <span className="text-xs text-muted-foreground">
        ({count})
      </span>
    </div>
  );
}

export function RatingPanel({ targetType, targetId, onRated }) {
  const { t } = useI18n();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  const [reviewerName, setReviewerName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/ratings`, {
        params: { target_type: targetType, target_id: targetId },
      });
      setRatings(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [targetType, targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!stars || !reviewerName.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/ratings`, {
        target_type: targetType,
        target_id: targetId,
        stars,
        reviewer_name: reviewerName,
        comment: comment || null,
      });
      toast.success(t("rating_posted"));
      setStars(0);
      setReviewerName("");
      setComment("");
      await load();
      onRated?.();
    } catch {
      toast.error(t("error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={submit}
        className="rounded-md border border-border bg-white p-5 space-y-4"
        data-testid={`rating-form-${targetType}-${targetId}`}
      >
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">
            {t("leave_rating")}
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setStars(n)}
                data-testid={`star-${n}`}
                className="p-1 hover:scale-110 transition-transform"
              >
                <Star
                  className={`h-7 w-7 ${
                    n <= (hover || stars)
                      ? "text-accent fill-accent"
                      : "text-muted-foreground/30"
                  }`}
                  strokeWidth={2}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
            {t("your_name")}
          </Label>
          <Input
            data-testid="rating-reviewer-name"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            placeholder={t("your_name_placeholder")}
            className="rounded-md h-10"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
            {t("comment_optional")}
          </Label>
          <Textarea
            data-testid="rating-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("comment_placeholder")}
            rows={2}
            className="rounded-md resize-none"
          />
        </div>

        <Button
          type="submit"
          data-testid="submit-rating-btn"
          disabled={!stars || !reviewerName.trim() || submitting}
          className="w-full rounded-md h-10 gap-2"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4" /> {t("submit_rating")}
            </>
          )}
        </Button>
      </form>

      {!loading && ratings.length > 0 && (
        <div className="space-y-3">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
            {ratings.length} {t("reviews")}
          </div>
          {ratings.map((r) => (
            <div
              key={r.id}
              data-testid={`rating-${r.id}`}
              className="rounded-md border border-border bg-white p-4"
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-semibold text-sm">{r.reviewer_name}</div>
                <RatingStars value={r.stars} />
              </div>
              {r.comment && (
                <p className="text-sm text-foreground/80 leading-relaxed">{r.comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
